/* eslint-disable no-unused-vars */

import { TNameTitle } from '@interfaces/common/common';
import { TValueOfStringRecord } from '@interfaces/common/genericWithModifying';

export const ELockedUnlocked = {
	locked: 'LOCKED',
	unlocked: 'UNLOCKED',
} as const;

export const EButtonType = {
	submit: 'submit',
	button: 'button',
	reset: 'reset',
} as const;

export const ESnackbarTypes = {
	success: 'success',
	error: 'error',
	warning: 'warning',
	info: 'info',
} as const;

export const EBrowser = {
	microsoftEdge: 'Microsoft Edge',
	microsoftInternetExplorer: 'Microsoft Internet Explorer',
	mozillaFirefox: 'Mozilla Firefox',
	opera: 'Opera',
	ucBrowser: 'UC Browser',
	samsungBrowser: 'Samsung Browser',
	googleChrome: 'Google Chrome',
	appleSafari: 'Apple Safari',
} as const;

export const ESortDirection = {
	asc: 'ASC',
	desc: 'DESC',
} as const;

export const EStatus = {
	active: 'active',
	blocked: 'blocked',
} as const;

export const EUserRoles = {
	defaultRole: 'DEFAULT_ROLE',
	platformOwnerAdministrator: 'PLATFORM_OWNER_ADMINISTRATOR',
	portalOwner: 'PORTAL_OWNER',
	portalProjectAdministrator: 'PORTAL_PROJECT_ADMIN',
	portalProjectMember: 'PORTAL_PROJECT_MEMBER',
	assetOwnerOrganizationAdministrator: 'ASSET_OWNER_ORGANIZATION_ADMINISTRATOR',
	assetOwnerApprovalUnit: 'ASSET_OWNER_APPROVAL_UNIT',
	assetOwnerDefaultContact: 'ASSET_OWNER_DEFAULT_CONTACT',
	assetOwnerContact: 'ASSET_OWNER_CONTACT',
	assetOwnerViewer: 'ASSET_OWNER_VIEWER',
	assetOwnerAttributeUpdater: 'ASSET_OWNER_ATTRIBUTE_UPDATER',
	assetOwnerSignatory: 'ASSET_OWNER_SIGNATORY',
	assetOwnerManageRequests: 'ASSET_OWNER_MANAGE_REQUESTS',
	assetConsumerOrganizationAdministrator: 'ASSET_CONSUMER_ORGANIZATION_ADMINISTRATOR',
	assetConsumerAssetEnquirer: 'ASSET_CONSUMER_ASSET_ENQUIRER',
	assetConsumerAssetViewer: 'ASSET_CONSUMER_ASSET_VIEWER',
	networkOperatorOrganizationAdministrator: 'NETWORK_OPERATOR_ORGANIZATION_ADMINISTRATOR',
	networkOperatorAssetEnquirer: 'NETWORK_OPERATOR_ASSET_ENQUIRER',
	networkOperatorAssetViewer: 'NETWORK_OPERATOR_ASSET_VIEWER',
	networkOperatorTelecomSitesViewer: 'NETWORK_OPERATOR_SITE_VIEWER',
	networkOperatorTelecomSitesAttributeUpdater: 'NETWORK_OPERATOR_SITE_ATTRIBUTE_UPDATER',
	networkOperatorKpiViewer: 'NETWORK_OPERATOR_KPI_VIEWER',
	networkOperatorThresholdKpiViewer: 'NETWORK_OPERATOR_KPI_THRESHOLDS_VIEWER',
	networkOperatorKpiUpdater: 'NETWORK_OPERATOR_KPI_UPDATER',
	networkOperatorThresholdKpiUpdater: 'NETWORK_OPERATOR_KPI_THRESHOLDS_UPDATER',
	networkOperatorCmModuleEditor: 'NETWORK_OPERATOR_CM_MODULE_EDITOR',
	networkOperatorCmModuleViewer: 'NETWORK_OPERATOR_CM_MODULE_VIEWER',
	networkOperatorCmModuleUpdater: 'NETWORK_OPERATOR_CM_MODULE_UPDATER',
	networkOperatorCmModuleAdmin: 'NETWORK_OPERATOR_CM_MODULE_ADMIN',
	networkOperatorAlarmManager: 'NETWORK_OPERATOR_ALARM_MANAGER',
	smoLightViewer: 'SMO_LIGHT_VIEWER',
	smoLightUpdater: 'SMO_LIGHT_UPDATER',
	aiAccessToPmData: 'AI_ACCESS_TO_PM_DATA',
} as const;

export const RolesWithAccessToMap = [
	EUserRoles.assetOwnerContact,
	EUserRoles.assetOwnerDefaultContact,
	EUserRoles.assetOwnerViewer,
	EUserRoles.assetOwnerAttributeUpdater,
	EUserRoles.assetOwnerSignatory,
	EUserRoles.assetOwnerManageRequests,
	EUserRoles.assetOwnerApprovalUnit,
	EUserRoles.networkOperatorAssetEnquirer,
	EUserRoles.networkOperatorAssetViewer,
	EUserRoles.networkOperatorTelecomSitesViewer,
	EUserRoles.networkOperatorTelecomSitesAttributeUpdater,
	EUserRoles.networkOperatorKpiViewer,
	EUserRoles.networkOperatorKpiUpdater,
	EUserRoles.networkOperatorThresholdKpiUpdater,
	EUserRoles.networkOperatorThresholdKpiViewer,
	EUserRoles.networkOperatorCmModuleViewer,
	EUserRoles.networkOperatorCmModuleEditor,
];

export type TKeyOfUserRoles = keyof typeof EUserRoles;

export const defaultMeta = {
	page: 1,
	pageSize: 10,
};

export const EOrganizationType = {
	networkOperator: 'NETWORK_OPERATOR',
	assetOwner: 'ASSET_OWNER',
	assetConsumer: 'ASSET_CONSUMER',
} as const;

export const EActionMeta = {
	removeValue: 'remove-value',
	popValue: 'pop-value',
	clear: 'clear',
} as const;

export const EMIMETypes = {
	doc: 'application/msword',
	docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	xls: 'application/vnd.ms-excel',
	xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	txt: 'text/plain',
	ppt: 'application/vnd.ms-powerpoint',
	pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
	jpeg: 'image/jpeg',
	png: 'image/png',
	pdf: 'application/pdf',
	csv: 'text/csv',
	svg: 'image/svg+xml',
	zip: 'application/zip',
	xml: 'application/xml',
	kml: 'application/vnd.google-earth.kml+xml',
	kmz: 'application/vnd.google-earth.kmz',
	json: 'application/json',
	yang: 'application/yang',
} as const;

export const EFileExtensions = {
	doc: '.doc',
	docx: '.docx',
	xls: '.xls',
	xlsx: '.xlsx',
	txt: '.txt',
	ppt: '.ppt',
	pptx: '.pptx',
	jpeg: '.jpeg',
	jpg: '.jpg',
	png: '.png',
	pdf: '.pdf',
	csv: '.csv',
	svg: '.svg',
	zip: '.zip',
	kml: '.kml',
	kmz: '.kmz',
	json: '.json',
	xml: '.xml',
	yang: '.yang',
} as const;

export const ETechnologiesValues = {
	T_4G_TDD: 'T_4G_TDD',
	T_4G_FDD: 'T_4G_FDD',
	T_4G: 'T_4G',
	T_5G: 'T_5G',
	C_4G_TDD: 'C_4G_TDD',
	C_4G_FDD: 'C_4G_FDD',
	C_5G: 'C_5G',
	C_IMS: 'C_IMS',
	C_MME: 'C_MME',
} as const;

export type TTechnologyValuesArray = Array<TTechnologiesValues>;

export const ETechnologiesLabels = {
	T_4G_TDD: '4G TDD',
	T_4G_FDD: '4G FDD',
	T_5G: '5G',
	T_4G: '4G',
	C_4G_TDD: '4G TDD',
	C_4G_FDD: '4G FDD',
	C_5G: '5G',
	C_IMS: 'IMS',
	C_MME: 'MME',
	T_5G_ORAN: '5G O-RAN',
} as const;

export const ETechnologyColors = {
	T_5G: '#108F61',
	T_4G_TDD: '#A70082',
	T_4G_FDD: '#781A8F',
} as const;

export const EVendorsValues = {
	airspan: 'AIRSPAN',
	benetel: 'BENETEL',
	flexidas: 'FLEXI_DAS',
	limenet: 'LIMENET',
	mavenir: 'MAVENIR',
	nokia: 'NOKIA',
	oran: 'AWTG_ORAN',
} as const;

export const EVendorsLabels = {
	airspan: 'Airspan',
	benetel: 'Benetel',
	flexidas: 'Flexi-DAS',
	limenet: 'Limenet',
	mavenir: 'Mavenir',
	nokia: 'Nokia',
	oran: 'AWTG RAN',
} as const;

export const EMenuActionsTypes = {
	all: 'all',
	allFiltered: 'allFiltered',
	currentPage: 'currentPage',
	selected: 'selected',
} as const;

export const EExportFileTypes = {
	csv: 'CSV',
	kml: 'KML',
	shape: 'SHAPE',
	xls: 'XLS',
	xlsx: 'XLSX',
	kmz: 'KMZ',
	json: 'JSON',
} as const;

export const EKeycloakEventTypes = {
	onReady: 'onReady',
	onInitError: 'onInitError',
	onAuthSuccess: 'onAuthSuccess',
	onAuthError: 'onAuthError',
	onAuthRefreshSuccess: 'onAuthRefreshSuccess',
	onAuthRefreshError: 'onAuthRefreshError',
	onTokenExpired: 'onTokenExpired',
	onAuthLogout: 'onAuthLogout',
} as const;

export const EConnectionStatuses = {
	CONNECTED: 'CONNECTED',
	DISCONNECTED: 'DISCONNECTED',
	PENDING: 'PENDING',
} as const;

export const EMultiOrgUserStatuses = {
	allOff: 'ALL_OFF',
	allOffDisabled: 'ALL_OFF_DISABLED',
	allOn: 'ALL_ON',
	allOnDisabled: 'ALL_ON_DISABLED',
	mixed: 'MIXED',
} as const;

export const ECrowdCellConnectionType = {
	OFFLINE: 'OFFLINE',
	ONLINE: 'ONLINE',
} as const;

export const ENodeTypeValues = {
	enb: 'ENB',
	ims: 'IMS',
	mme: 'MME',
	amf: 'AMF',
} as const;

export const EActionButtonTypes = {
	approve: 'approve',
	disapprove: 'disapprove',
	revoke: 'revoke',
	sleep: 'sleep',
} as const;

export const ESortingOptionValues = {
	desc: 'DESC',
	asc: 'ASC',
} as const;

export const ETimeAggregationTypes = {
	seconds: 'seconds',
	interval: '15 minutes',
	minutes: 'minutes',
	hours: 'hours',
	days: 'days',
	months: 'months',
	hourMinutes: 'hourMinutes',
	dayMonthYear: 'dayMonthYear',
} as const;

export const ECoordFormat = {
	latlon: 'latlon',
	utm: 'utm',
} as const;

export interface IOrganisationRoleInterface {
	header: string;
	roles: Array<TNameTitle<TUserRoles, string>>;
}

export const EGroupMenuTitles = {
	AccessManagement: 'Access Management',
	AcquireAssets: 'Acquire Assets',
	Alarm: 'Fault Management',
	Assets: 'Assets',
	ConfigurationManagement: 'Configuration Management',
	KPI: 'Performance Management',
	Settings: 'Settings',
	TelecomSites: 'Telecom Sites',
	AssetTypesManagement: 'Asset Types Management',
	AssetsDashboard: 'Dashboard',
	CMModule: 'Configuration Management',
	ConfigurationDataModelManagement: 'Configuration Data Model Management',
	Reports: 'Reports',
	SMOLight: 'SMO',
	Portal: 'Portal',
} as const;

export const EYupErrorTypes = {
	max: 'max',
	min: 'min',
	required: 'required',
} as const;

export const EServerTaskStatus = {
	error: 'ERROR',
	success: 'SUCCESS',
	started: 'STARTED',
} as const;

export const EApiDocsGroups = {
	flex5G: 'FLEX5G',
	iDams: 'IDAMS',
	other: 'OTHER',
} as const;

export const ESubjectType = {
	alarms: 'ALARMS',
	asset: 'ASSET',
	acquiredAsset: 'ACQUIRED_ASSET',
	polygon: 'POLYGON',
	site: 'SITE',
	user: 'USER',
	dataSources: 'DATA_SOURCES',
	changeMonitoring: 'CHANGE_MONITORING',
	ruConfiguration: 'RU_CONFIGURATION',
} as const;

export const EDefaultValueType = {
	array: 'ARRAY',
	arrayCreatable: 'ARRAYCREATABLE',
	boolean: 'BOOLEAN',
	string: 'STRING',
	date: 'DATE',
	digits: 'DIGITS',
} as const;

export const EPolygonTypeDTO = {
	circle: 'CIRCLE',
	polygon: 'POINT',
} as const;

export const EPromiseResultStatuses = {
	fulfilled: 'fulfilled',
	rejected: 'rejected',
} as const;

export const EControlsType = {
	filters: 'filters',
	filtersOokla: 'filtersOokla',
	mapSettings: 'mapSettings',
	owners: 'owners',
	customPolygonControls: 'customPolygonControls',
	customPolygonList: 'customPolygonList',
	createAssetMode: 'createAssetMode',
	dashboardKpi: 'dashboardKpi',
	sectors: 'sectors',
} as const;

export const ESitesFilterSections = {
	technologies: 'technologies',
	siteTypes: 'siteTypes',
} as const;

export const EAdminPanelPageTitles = {
	APIDocumentation: 'API Documentation',
	AlarmManagement: 'Alarm Control',
	Alarms: 'Alarms',
	AllSitesCells: 'All sites/cells',
	ApprovalUnitAssignment: 'Approval Unit Assignment',
	ConfigurationComparison: 'Configuration Comparison',
	ConsistencyCheck: 'Consistency Check',
	Dashboard: 'Dashboard',
	DataSources: 'Data Sources',
	DownloadHistory: 'Download History',
	HistoryCheck: 'History Check',
	IncomingRequests: 'Incoming Requests',
	KPIAIAnalytics: 'AI Analytics',
	KPIDashboard: 'KPI Dashboard',
	KPINotifications: 'KPI Alerts',
	KPIThresholds: 'KPI Thresholds',
	ManageAcquiredAssets: 'Manage Acquired Assets',
	ManageApprovalUnits: 'Manage Approval Units',
	ManageAssetTypes: 'Manage Asset Types',
	ManageAssets: 'Manage Assets',
	ManageErrorCodes: 'Manage Error Codes',
	ManageFlex5GUnits: 'Manage Flex-5G Units',
	ManageNetworkElementConfigurations: '5G RAN CM',
	ManageOrganisations: 'Manage Organisations',
	ManagePlatformBranding: 'Manage Platform Branding',
	ManageSitesDB: 'Manage Sites DB',
	ManageUsers: 'Manage Users',
	NetworkElements: 'Network Elements',
	OrganisationSettings: 'Organisation Settings',
	OutgoingRequests: 'Outgoing Requests',
	Projects: 'Projects',
	RequestList: 'Request list',
	Ruckus: 'RUCKUS One',
	RUStatusDashboard: 'RU Status Dashboard',
	RUConfiguration: 'RU Configuration',
	SMOLightMiddleware: 'SMO Middleware',
	Schema: 'Schema',
	ConfigurationTemplates: 'Configuration Templates',
	OperationRules: 'Operation Window Rules',
	SectorsSettings: 'Sectors Settings',
	UserHistory: 'User History',
	VESEvents: 'Events Log',
};

export const EEnabledDisabled = {
	enabled: 'ENABLED',
	disabled: 'DISABLED',
} as const;

export const EWeekDayNames = {
	monday: 'MONDAY',
	tuesday: 'TUESDAY',
	wednesday: 'WEDNESDAY',
	thursday: 'THURSDAY',
	friday: 'FRIDAY',
	saturday: 'SATURDAY',
	sunday: 'SUNDAY',
} as const;

export const EWeekDayTitles = {
	monday: 'Monday',
	tuesday: 'Tuesday',
	wednesday: 'Wednesday',
	thursday: 'Thursday',
	friday: 'Friday',
	saturday: 'Saturday',
	sunday: 'Sunday',
} as const;

export const EConsistencyParametersPathStatus = {
	inBoth: 'IN_BOTH',
	missing: 'MISSING',
	extra: 'EXTRA_IN_CONFIG',
} as const;

export const EDefaultRestrictedAccess = {
	default: 'DEFAULT',
	restricted: 'RESTRICTED',
} as const;

export const EResultingAccessTypes = {
	granted: 'GRANTED',
	denied: 'DENIED',
} as const;

export type TResultingAccessTypes = TValueOfStringRecord<typeof EResultingAccessTypes>
export type TDefaultRestrictedAccess = TValueOfStringRecord<typeof EDefaultRestrictedAccess>
export type TSitesFilterSections = TValueOfStringRecord<typeof ESitesFilterSections>
export type TEControlsType = TValueOfStringRecord<typeof EControlsType>
export type TDefaultValueType = TValueOfStringRecord<typeof EDefaultValueType>
export type TSubjectType = TValueOfStringRecord<typeof ESubjectType>
export type TApiDocsGroups = TValueOfStringRecord<typeof EApiDocsGroups>
export type TServerTaskStatus = TValueOfStringRecord<typeof EServerTaskStatus>
export type TYupErrorTypes = TValueOfStringRecord<typeof EYupErrorTypes>
export type TGroupMenuTitles = TValueOfStringRecord<typeof EGroupMenuTitles>
export type TCoordFormat = TValueOfStringRecord<typeof ECoordFormat>
export type TTimeAggregationTypes = TValueOfStringRecord<typeof ETimeAggregationTypes>
export type TSortingOptionValues = TValueOfStringRecord<typeof ESortingOptionValues>
export type TActionButtonTypes = TValueOfStringRecord<typeof EActionButtonTypes>
export type TNodeTypeValues = TValueOfStringRecord<typeof ENodeTypeValues>
export type TCrowdCellConnectionType = TValueOfStringRecord<typeof ECrowdCellConnectionType>
export type TKeycloakEventTypes = TValueOfStringRecord<typeof EKeycloakEventTypes>
export type TExportFileTypes = TValueOfStringRecord<typeof EExportFileTypes>
export type TMenuActionsTypes = TValueOfStringRecord<typeof EMenuActionsTypes>
export type TVendorsLabels = TValueOfStringRecord<typeof EVendorsLabels>
export type TVendorsValues = TValueOfStringRecord<typeof EVendorsValues>
export type TTechnologiesLabels = TValueOfStringRecord<typeof ETechnologiesLabels>
export type TTechnologiesValues = TValueOfStringRecord<typeof ETechnologiesValues>
export type TFileExtensions = TValueOfStringRecord<typeof EFileExtensions>
export type TMIMETypes = TValueOfStringRecord<typeof EMIMETypes>
export type TActionMetaconst = TValueOfStringRecord<typeof EActionMeta>
export type TOrganizationType = TValueOfStringRecord<typeof EOrganizationType>
export type TUserRoles = TValueOfStringRecord<typeof EUserRoles>
export type TStatus = TValueOfStringRecord<typeof EStatus>
export type TSortDirection = TValueOfStringRecord<typeof ESortDirection>
export type TSnackbarTypes = TValueOfStringRecord<typeof ESnackbarTypes>
export type TBrowser = TValueOfStringRecord<typeof EBrowser>
export type TButtonType = TValueOfStringRecord<typeof EButtonType>
export type TPolygonTypeDTO = TValueOfStringRecord<typeof EPolygonTypeDTO>
export type TTechnologyColors = TValueOfStringRecord<typeof ETechnologyColors>
export type TConnectionStatuses = TValueOfStringRecord<typeof EConnectionStatuses>

